import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useStore } from '../../../stores/root';
import styles from './styles.module.scss';
import { 
    IOperationItem, 
    TOperationActivityType, 
    TOperationStatusFilter, 
    TOperationTypeFilter, 
    TOperationViewMode 
} from './types';
import WorkspaceHeader from './header';
import WorkspaceTableView from './views/table';
import WorkspaceBoardView from './views/board';
import RescheduleModal from './modals/reschedule';
import QuickStatusModal from './modals/quickStatus';
import ActivitiesService from '../../../services/activities/activities';
import { IActivityResourceShortProps } from '../../../props/activities';
import LoadingComponent from '../../feedbacks/loading';
import ErrorService from '../../../services/general/error';
import { faAnchor, faBox, faBoxesPacking, faCheck, faDolly, faShip, faTag, faTruck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import OrderService from '../../../services/orders/order';

const OperationsWorkspace: React.FC = () => {
    const { banner } = useStore();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [activities, setActivities] = useState<IActivityResourceShortProps | undefined>();

    // Filter & Navigation States
    const [selectedDate, setSelectedDate] = useState<{ startDate: string; endDate: string }>({
        startDate: moment().startOf('day').toISOString(),
        endDate: moment().endOf('day').toISOString(),
    });
    const [statusFilter, setStatusFilter] = useState<TOperationStatusFilter>('all');
    const [typeFilter, setTypeFilter] = useState<TOperationTypeFilter>('all');
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [viewMode, setViewMode] = useState<TOperationViewMode>('table');

    // Modal States
    const [activeRescheduleItem, setActiveRescheduleItem] = useState<IOperationItem | undefined>();
    const [activeStatusItem, setActiveStatusItem] = useState<IOperationItem | undefined>();

    useEffect(() => {
        loadActivities();
    }, [selectedDate]);

    const loadActivities = async () => {
        try {
            setLoaded(false);
            const qs = `startDate=${selectedDate.startDate}&endDate=${selectedDate.endDate}`;
            const res = await ActivitiesService.retrieve(qs);
            setActivities(res);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'load_operations_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Gagal memuat data operasional. Error: ' + ErrorService.getMessage(e),
            });
            setLoaded(true);
        }
    };

    // Normalize raw backend activities into unified IOperationItem[]
    // Tahap 3 Guardrail: Limit active operational overdue to 60 days, and strictly filter out stale items (> 60 days) outside the selected period.
    const MAX_ACTIVE_OVERDUE_DAYS = 60;

    const allItems: IOperationItem[] = useMemo(() => {
        if (!activities) return [];
        const items: IOperationItem[] = [];
        const today = moment().startOf('day');
        const selStart = moment(selectedDate.startDate).startOf('day');
        const selEnd = moment(selectedDate.endDate).endOf('day');

        // 1. Loading Trackings
        (activities.loading || []).forEach((loading) => {
            const orderNum = loading.order?.orderNumber || loading.orderNumber;
            const hasOrder = !!(loading.orderId || orderNum || loading.order);
            if (!hasOrder) return; // Only show items with published/active order

            const details = loading.loadDetails;
            const dateStr = details?.actualStartDate || details?.estimatedStartDate || loading.createdAt || moment().toISOString();
            const itemDate = moment(dateStr);
            const rawStatus = (loading.status || 'scheduled').toLowerCase();
            const isDone = rawStatus === 'completed' || rawStatus === 'done';
            const isInProg = rawStatus === 'in_progress' || rawStatus === 'running';
            const rawOverdue = !isDone && itemDate.isBefore(today, 'day');
            const overdueDays = rawOverdue ? today.diff(itemDate, 'days') : 0;
            const isOverdue = rawOverdue && overdueDays <= MAX_ACTIVE_OVERDUE_DAYS;

            // Guardrail: Exclude stale items (> 60 days) that fall outside the selected date range
            const isWithinSelectedRange = itemDate.isBetween(selStart, selEnd, undefined, '[]');
            if (overdueDays > MAX_ACTIVE_OVERDUE_DAYS && !isWithinSelectedRange) {
                return;
            }

            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (loading.orderId ? `Order #${loading.orderId.substring(0, 8)}` : '');

            items.push({
                id: `load_${loading.id}`,
                rawId: loading.id,
                itemType: 'tracking',
                activityType: 'loading',
                activityLabel: 'Muat Depo (Loading)',
                activityColor: '#f59e0b',
                activityIcon: faBoxesPacking,
                orderId: loading.orderId,
                orderNumber: orderLabel,
                customerName: (loading.order as any)?.customer?.name,
                containers: loading.container ? [{
                    id: loading.container.id,
                    containerNumber: loading.container.containerNumber || 'No Container',
                    sealNumber: loading.container.sealNumber,
                    size: loading.container.size
                }] : [],
                locationText: details?.address && details.address !== 'ededed' ? details.address : 'Depo / Gudang Muat',
                scheduledDate: dateStr,
                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),
                actualDate: details?.actualStartDate,
                isOverdue,
                overdueDays,
                status: rawStatus,
                normalizedStatus: isDone ? 'completed' : (isInProg ? 'in_progress' : (isOverdue ? 'overdue' : 'scheduled')),
                statusLabel: isDone ? 'Selesai' : (isInProg ? 'Sedang Muat' : (isOverdue ? 'Overdue Muat' : (rawOverdue ? 'Terlewat Lama' : 'Terjadwal'))),
                detailsLink: `/orders/${loading.orderId}/trackings`,
                orderLink: `/orders/${loading.orderId}`,
                rawObject: loading,
            });
        });

        // 2. Unloading Trackings
        (activities.unloading || []).forEach((unloading) => {
            const orderNum = unloading.order?.orderNumber || unloading.orderNumber;
            const hasOrder = !!(unloading.orderId || orderNum || unloading.order);
            if (!hasOrder) return; // Only show items with published/active order

            const details = unloading.unloadingDetails;
            const dateStr = details?.actualStartDate || details?.estimatedStartDate || unloading.createdAt || moment().toISOString();
            const itemDate = moment(dateStr);
            const rawStatus = (unloading.status || 'scheduled').toLowerCase();
            const isDone = rawStatus === 'completed' || rawStatus === 'done';
            const isInProg = rawStatus === 'in_progress' || rawStatus === 'running';
            const rawOverdue = !isDone && itemDate.isBefore(today, 'day');
            const overdueDays = rawOverdue ? today.diff(itemDate, 'days') : 0;
            const isOverdue = rawOverdue && overdueDays <= MAX_ACTIVE_OVERDUE_DAYS;

            // Guardrail: Exclude stale items (> 60 days) that fall outside the selected date range
            const isWithinSelectedRange = itemDate.isBetween(selStart, selEnd, undefined, '[]');
            if (overdueDays > MAX_ACTIVE_OVERDUE_DAYS && !isWithinSelectedRange) {
                return;
            }

            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (unloading.orderId ? `Order #${unloading.orderId.substring(0, 8)}` : '');

            items.push({
                id: `unload_${unloading.id}`,
                rawId: unloading.id,
                itemType: 'tracking',
                activityType: 'unloading',
                activityLabel: 'Bongkar (Unloading)',
                activityColor: '#8b5cf6',
                activityIcon: faDolly,
                orderId: unloading.orderId,
                orderNumber: orderLabel,
                customerName: (unloading.order as any)?.customer?.name,
                containers: unloading.container ? [{
                    id: unloading.container.id,
                    containerNumber: unloading.container.containerNumber || 'No Container',
                    sealNumber: unloading.container.sealNumber,
                    size: unloading.container.size
                }] : [],
                locationText: details?.address && details.address !== 'ededed' ? details.address : 'Pelabuhan / Gudang Bongkar',
                scheduledDate: dateStr,
                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),
                actualDate: details?.actualStartDate,
                isOverdue,
                overdueDays,
                status: rawStatus,
                normalizedStatus: isDone ? 'completed' : (isInProg ? 'in_progress' : (isOverdue ? 'overdue' : 'scheduled')),
                statusLabel: isDone ? 'Selesai' : (isInProg ? 'Sedang Bongkar' : (isOverdue ? 'Overdue Bongkar' : (rawOverdue ? 'Terlewat Lama' : 'Terjadwal'))),
                detailsLink: `/orders/${unloading.orderId}/trackings`,
                orderLink: `/orders/${unloading.orderId}`,
                rawObject: unloading,
            });
        });

        // 3. Dooring Trackings
        (activities.dooring || []).forEach((dooring) => {
            const orderNum = dooring.order?.orderNumber || dooring.orderNumber;
            const hasOrder = !!(dooring.orderId || orderNum || dooring.order);
            if (!hasOrder) return; // Only show items with published/active order

            const details = dooring.dooringDetails;
            const departureDate = details?.actualDepartureDate || details?.estimatedDepartureDate;
            const arrivalDate = details?.actualArrivalDate || details?.estimatedArrivalDate;
            const dateStr = departureDate || dooring.createdAt || moment().toISOString();
            const itemDate = moment(dateStr);
            const rawStatus = (dooring.status || 'scheduled').toLowerCase();
            const isDone = rawStatus === 'completed' || rawStatus === 'arrived' || rawStatus === 'done';
            const isInProg = rawStatus === 'in_progress' || rawStatus === 'in_transit' || rawStatus === 'departed';

            let rawOverdue = false;
            if (!isDone) {
                if (isInProg && arrivalDate) {
                    rawOverdue = moment(arrivalDate).startOf('day').isBefore(today, 'day');
                } else if (!isInProg) {
                    rawOverdue = itemDate.isBefore(today, 'day');
                }
            }
            const overdueDays = rawOverdue 
                ? (isInProg && arrivalDate ? today.diff(moment(arrivalDate).startOf('day'), 'days') : today.diff(itemDate, 'days')) 
                : 0;
            const isOverdue = rawOverdue && overdueDays <= MAX_ACTIVE_OVERDUE_DAYS;

            // Guardrail: Exclude stale items (> 60 days) that fall outside the selected date range
            const isWithinSelectedRange = itemDate.isBetween(selStart, selEnd, undefined, '[]') || 
                (arrivalDate ? moment(arrivalDate).isBetween(selStart, selEnd, undefined, '[]') : false);
            if (overdueDays > MAX_ACTIVE_OVERDUE_DAYS && !isWithinSelectedRange) {
                return;
            }

            const orderLabel = orderNum ? `#${OrderService.getOrderNumber(String(orderNum))}` : (dooring.orderId ? `Order #${dooring.orderId.substring(0, 8)}` : '');

            items.push({
                id: `door_${dooring.id}`,
                rawId: dooring.id,
                itemType: 'tracking',
                activityType: 'dooring',
                activityLabel: 'Dooring (Trucking)',
                activityColor: '#10b981',
                activityIcon: faTruck,
                truckRegNumber: details?.truck?.registrationNumber,
                vendorName: details?.truck?.vendor?.name,
                driverName: details?.driverName,
                driverPhone: details?.driverPhone,
                orderId: dooring.orderId,
                orderNumber: orderLabel,
                customerName: (dooring.order as any)?.customer?.name,
                containers: dooring.container ? [{
                    id: dooring.container.id,
                    containerNumber: dooring.container.containerNumber || 'No Container',
                    sealNumber: dooring.container.sealNumber,
                    size: dooring.container.size
                }] : [],
                originAddress: details?.originAddress,
                destinationAddress: details?.destinationAddress,
                locationText: details?.originAddress && details?.destinationAddress 
                    ? `${details.originAddress} ➔ ${details.destinationAddress}` 
                    : (details?.destinationAddress || 'Alamat Penerima'),
                scheduledDate: dateStr,
                scheduledDateFormatted: itemDate.format('DD/MM/YYYY HH:mm'),
                estimatedArrival: details?.estimatedArrivalDate ? moment(details.estimatedArrivalDate).format('DD/MM/YYYY') : undefined,
                actualDate: details?.actualArrivalDate || details?.actualDepartureDate,
                isOverdue,
                overdueDays,
                status: rawStatus,
                normalizedStatus: isDone ? 'completed' : (isInProg ? 'in_progress' : (isOverdue ? 'overdue' : 'scheduled')),
                statusLabel: isDone ? 'Tiba di Tujuan' : (isInProg ? 'Dooring Dalam Perjalanan' : (isOverdue ? 'Overdue Dooring' : (rawOverdue ? 'Terlewat Lama' : 'Terjadwal'))),
                detailsLink: `/orders/${dooring.orderId}/trackings`,
                orderLink: `/orders/${dooring.orderId}`,
                rawObject: dooring,
            });
        });

        // 4. Ship Schedules
        (activities.shipSchedules || []).forEach((schedule) => {
            const orders = (schedule.orders || []).filter(o => o && (o.orderNumber || o.id));
            if (orders.length < 1) return; // Only show ship schedules that are attached/related to orders

            const departureDate = schedule.actualDeparture || schedule.estimatedDeparture;
            const arrivalDate = schedule.actualArrival || schedule.estimatedArrival;
            const dateStr = departureDate || schedule.createdAt || moment().toISOString();
            const itemDate = moment(dateStr);
            const rawStatus = (schedule.status || 'scheduled').toLowerCase();

            const isSailing = rawStatus === 'sailing' || rawStatus === 'in_progress';
            const isArrived = rawStatus === 'arrived' || rawStatus === 'completed';
            const isDone = isArrived;

            let rawOverdue = false;
            if (!isDone) {
                if (isSailing && arrivalDate) {
                    rawOverdue = moment(arrivalDate).startOf('day').isBefore(today, 'day');
                } else if (!isSailing) {
                    rawOverdue = itemDate.isBefore(today, 'day');
                }
            }
            const overdueDays = rawOverdue 
                ? (isSailing && arrivalDate ? today.diff(moment(arrivalDate).startOf('day'), 'days') : today.diff(itemDate, 'days')) 
                : 0;
            const isOverdue = rawOverdue && overdueDays <= MAX_ACTIVE_OVERDUE_DAYS;

            // Guardrail: Exclude stale items (> 60 days) that fall outside the selected date range
            const isWithinSelectedRange = itemDate.isBetween(selStart, selEnd, undefined, '[]') || 
                (arrivalDate ? moment(arrivalDate).isBetween(selStart, selEnd, undefined, '[]') : false);
            if (overdueDays > MAX_ACTIVE_OVERDUE_DAYS && !isWithinSelectedRange) {
                return;
            }

            const firstOrder = orders.length > 0 ? orders[0] : undefined;
            const orderLabel = firstOrder ? `#${OrderService.getOrderNumber(String(firstOrder.orderNumber))}${orders.length > 1 ? ` (+${orders.length - 1})` : ''}` : '';

            let shipColor = '#0284c7'; // Sky Blue for Scheduled Ship
            let shipLabel = 'Jadwal Kapal';
            if (isSailing) {
                shipColor = '#0d9488'; // Teal Green for Kapal Berlayar
                shipLabel = 'Kapal Berlayar';
            } else if (isArrived) {
                shipColor = '#059669'; // Emerald Green for Kapal Sandar
                shipLabel = 'Kapal Sandar';
            }

            const normalizedStatus = isDone ? 'completed' : (isSailing ? 'in_progress' : (isOverdue ? 'overdue' : 'scheduled'));
            const statusLabel = isSailing 
                ? 'Sedang Berlayar' 
                : (isArrived ? 'Sudah Sandar' : (isDone ? 'Selesai' : (isOverdue ? 'Overdue Keberangkatan' : (rawOverdue ? 'Terlewat Lama' : 'Terjadwal'))));

            items.push({
                id: `ship_${schedule.id}`,
                rawId: schedule.id,
                itemType: 'ship',
                activityType: 'ship_departure',
                activityLabel: shipLabel,
                activityColor: shipColor,
                activityIcon: faShip,
                shipName: schedule.ship?.name || 'Kapal Pelayaran',
                voy: schedule.voy,
                vendorName: schedule.ship?.vendor?.name,
                orderId: firstOrder?.id,
                orderNumber: orderLabel || undefined,
                orders: orders.map(o => ({ id: o.id, orderNumber: String(o.orderNumber) })),
                originAddress: schedule.originAddress,
                destinationAddress: schedule.destinationAddress,
                locationText: schedule.originAddress && schedule.destinationAddress
                    ? `${schedule.originAddress} ➔ ${schedule.destinationAddress}`
                    : (schedule.originAddress || 'Pelabuhan Asal'),
                scheduledDate: dateStr,
                scheduledDateFormatted: `ETD: ${itemDate.format('DD/MM/YYYY')}`,
                estimatedArrival: arrivalDate ? moment(arrivalDate).format('DD/MM/YYYY') : undefined,
                actualDate: schedule.actualDeparture || schedule.actualArrival,
                isOverdue,
                overdueDays,
                status: rawStatus,
                normalizedStatus,
                statusLabel,
                detailsLink: `/operationals/ships/${schedule.ship?.id}/schedules/${schedule.id}`,
                orderLink: firstOrder ? `/orders/${firstOrder.id}` : undefined,
                rawObject: schedule,
            });
        });

        // Sort items: overdue first, then chronological
        return items.sort((a, b) => {
            if (a.isOverdue && !b.isOverdue) return -1;
            if (!a.isOverdue && b.isOverdue) return 1;
            return moment(a.scheduledDate).valueOf() - moment(b.scheduledDate).valueOf();
        });
    }, [activities, selectedDate]);

    // Base items filtered by Type & Search (used for KPI summary cards and as base for status filtering)
    const typeAndSearchFilteredItems = useMemo(() => {
        return allItems.filter((item) => {
            // Type Filter
            if (typeFilter === 'ship' && item.itemType !== 'ship') return false;
            if (typeFilter === 'loading' && item.activityType !== 'loading') return false;
            if (typeFilter === 'unloading' && item.activityType !== 'unloading') return false;
            if (typeFilter === 'dooring' && item.activityType !== 'dooring') return false;

            // Search Filter
            if (searchKeyword.trim() !== '') {
                const q = searchKeyword.toLowerCase();
                const matchShip = item.shipName?.toLowerCase().includes(q);
                const matchVoy = item.voy?.toLowerCase().includes(q);
                const matchOrder = item.orderNumber?.toLowerCase().includes(q) || item.orders?.some(o => o.orderNumber.toLowerCase().includes(q) || OrderService.getOrderNumber(o.orderNumber).toLowerCase().includes(q));
                const matchCust = item.customerName?.toLowerCase().includes(q);
                const matchLoc = item.locationText?.toLowerCase().includes(q);
                const matchTruck = item.truckRegNumber?.toLowerCase().includes(q);
                const matchDriver = item.driverName?.toLowerCase().includes(q);
                const matchContainer = item.containers?.some(c => c.containerNumber.toLowerCase().includes(q));

                if (!matchShip && !matchVoy && !matchOrder && !matchCust && !matchLoc && !matchTruck && !matchDriver && !matchContainer) {
                    return false;
                }
            }

            return true;
        });
    }, [allItems, typeFilter, searchKeyword]);

    // KPI Summary Counts (reacts dynamically to Type Filter & Search)
    const counts = useMemo(() => {
        let total = typeAndSearchFilteredItems.length;
        let overdue = typeAndSearchFilteredItems.filter(i => i.isOverdue).length;
        let inProgress = typeAndSearchFilteredItems.filter(i => i.normalizedStatus === 'in_progress').length;
        let completed = typeAndSearchFilteredItems.filter(i => i.normalizedStatus === 'completed').length;
        return { total, overdue, inProgress, completed };
    }, [typeAndSearchFilteredItems]);

    // Filtered Items for Table/Board view based on active status filter
    const filteredItems = useMemo(() => {
        return typeAndSearchFilteredItems.filter((item) => {
            // Status Filter
            if (statusFilter === 'overdue' && !item.isOverdue) return false;
            if (statusFilter === 'scheduled' && item.normalizedStatus !== 'scheduled') return false;
            if (statusFilter === 'in_progress' && item.normalizedStatus !== 'in_progress') return false;
            if (statusFilter === 'completed' && item.normalizedStatus !== 'completed') return false;

            return true;
        });
    }, [typeAndSearchFilteredItems, statusFilter]);

    const handleDateChange = (startDate: string, endDate: string) => {
        setSelectedDate({ startDate, endDate });
    };

    return (
        <div className={styles.container}>
            {/* Header, KPI Bar & Filters */}
            <WorkspaceHeader
                counts={counts}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                searchKeyword={searchKeyword}
                onSearchChange={setSearchKeyword}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRefresh={loadActivities}
            />

            {/* Main Content Area */}
            {!loaded ? (
                <LoadingComponent label="Memuat aktivitas operasional..." labelPosition="right" />
            ) : (
                <>
                    {viewMode === 'table' ? (
                        <WorkspaceTableView
                            items={filteredItems}
                            onQuickStatus={(item) => setActiveStatusItem(item)}
                            onReschedule={(item) => setActiveRescheduleItem(item)}
                        />
                    ) : (
                        <WorkspaceBoardView
                            items={filteredItems}
                            onQuickStatus={(item) => setActiveStatusItem(item)}
                            onReschedule={(item) => setActiveRescheduleItem(item)}
                        />
                    )}
                </>
            )}

            {/* Quick Status Modal */}
            {activeStatusItem && (
                <QuickStatusModal
                    item={activeStatusItem}
                    isOpen={true}
                    onDismiss={(refresh) => {
                        setActiveStatusItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}

            {/* Reschedule Modal */}
            {activeRescheduleItem && (
                <RescheduleModal
                    item={activeRescheduleItem}
                    isOpen={true}
                    onDismiss={(refresh) => {
                        setActiveRescheduleItem(undefined);
                        if (refresh) loadActivities();
                    }}
                />
            )}
        </div>
    );
};

export default OperationsWorkspace;
